import { type FC, useMemo, useState, useCallback } from "react";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useIsUserAuthenticatedWithRunnerForHost } from "@/queries/runner-queries";
import { useCreateServiceAccountAccessToken } from "@/queries/service-account-queries";
import { serviceAccountHeaders } from "@/api";
import { useEnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import { Button } from "@/components/flexkit/Button";
import { Alert, AlertDescription } from "@/components/podkit/alert/Alert";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { TrackLocations } from "@/hooks/use-segment";
import { serviceAccountDetailsUrl } from "@/utils/service-accounts";
import { Link } from "react-router-dom";

type PrebuildScmAccessCheckProps = {
    /** The executor subject (user or service account) */
    executor: PlainMessage<Subject>;
    /** The repository URL to check access for */
    repoUrl: string;
    /** The environment class IDs selected for prebuilds */
    prebuildEnvironmentClassIds: string[];
    /** Whether the user can update the project */
    canUpdateProject: boolean;
};

/**
 * Checks and displays SCM access status for the prebuild executor.
 * Shows a warning if the executor doesn't have access to the repository.
 */
export const PrebuildScmAccessCheck: FC<PrebuildScmAccessCheckProps> = ({
    executor,
    repoUrl,
    prebuildEnvironmentClassIds,
    canUpdateProject,
}) => {
    const { data: currentUser } = useAuthenticatedUser();
    const { environmentClasses } = useEnvironmentClassMap();

    // Get the runner ID from the first prebuild environment class
    const runnerId = useMemo(() => {
        if (prebuildEnvironmentClassIds.length === 0) {
            return undefined;
        }
        const firstClassId = prebuildEnvironmentClassIds[0];
        const entry = environmentClasses[firstClassId];
        return entry?.runner?.runnerId;
    }, [prebuildEnvironmentClassIds, environmentClasses]);

    // Get the SCM host from the repo URL
    const scmHost = useMemo(() => {
        if (!repoUrl) return undefined;
        try {
            return new URL(repoUrl).host;
        } catch {
            return undefined;
        }
    }, [repoUrl]);

    // Determine if the executor is the current user
    const isCurrentUser = executor.principal === Principal.USER && executor.id === currentUser?.id;

    // Determine if the executor is a service account
    const isServiceAccount = executor.principal === Principal.SERVICE_ACCOUNT;

    // Determine if the executor is another user (not current user)
    const isOtherUser = executor.principal === Principal.USER && executor.id !== currentUser?.id;

    // For service accounts, we need to get an access token to check their SCM access
    const { data: serviceAccountAccessToken } = useCreateServiceAccountAccessToken({
        serviceAccountId: executor.id,
        enabled: isServiceAccount,
    });

    const headers = useMemo(() => {
        if (!isServiceAccount || !serviceAccountAccessToken) return undefined;
        return serviceAccountHeaders({
            serviceAccountId: executor.id,
            serviceAccountAccessToken,
        });
    }, [isServiceAccount, serviceAccountAccessToken, executor.id]);

    // Check SCM access for current user
    const {
        data: currentUserAuthResponse,
        isPending: isPendingCurrentUserAuth,
    } = useIsUserAuthenticatedWithRunnerForHost(runnerId, scmHost, {
        enabled: isCurrentUser && !!runnerId && !!scmHost,
        refetchUntilAuthenticated: true,
    });

    // Check SCM access for service account
    // The headers.serviceAccountId is used in the query key to differentiate from user queries
    const {
        data: serviceAccountAuthResponse,
        isPending: isPendingServiceAccountAuth,
    } = useIsUserAuthenticatedWithRunnerForHost(runnerId, scmHost, {
        enabled: isServiceAccount && !!runnerId && !!scmHost && !!headers,
        headers,
        refetchUntilAuthenticated: true,
    });

    // Determine the auth response based on executor type
    const authResponse = isCurrentUser ? currentUserAuthResponse : isServiceAccount ? serviceAccountAuthResponse : undefined;
    // Only consider initial pending state, not refetches
    const isPending = isCurrentUser ? isPendingCurrentUserAuth : isServiceAccount ? isPendingServiceAccountAuth : false;

    // State for authentication modal
    const [showAuthModal, setShowAuthModal] = useState(false);

    const handleAuthenticate = useCallback(() => {
        setShowAuthModal(true);
    }, []);

    const handleAuthSuccess = useCallback(() => {
        setShowAuthModal(false);
    }, []);

    // Extract auth details for the modal
    const authDetails = useMemo(() => {
        if (authResponse?.type !== "AuthenticationRequired") return null;
        return {
            url: authResponse.url,
            patAuth: authResponse.patAuth,
            scmId: authResponse.scmId,
            scmName: authResponse.scmName,
        };
    }, [authResponse]);

    // Don't show anything if we don't have enough info
    if (!runnerId || !scmHost || !repoUrl) {
        return null;
    }

    // Don't show anything while initial load is pending (but not during refetches)
    if (isPending) {
        return null;
    }

    // For other users, we cannot check their access - just don't show anything
    if (isOtherUser) {
        return null;
    }

    // Check if authentication is required
    const isAuthenticationRequired = authResponse?.type === "AuthenticationRequired";

    if (!isAuthenticationRequired) {
        return null;
    }

    // Show warning for current user
    if (isCurrentUser && authDetails) {
        return (
            <>
                <Alert>
                    <IconWarningCircle className="text-content-warning" size="sm" />
                    <AlertDescription className="flex flex-col gap-3">
                        <span>
                            You don&apos;t have access to this repository on the selected runner. Prebuilds will fail
                            until you authenticate.
                        </span>
                        {canUpdateProject && (
                            <Button
                                variant="primary"
                                size="sm"
                                type="button"
                                onClick={handleAuthenticate}
                                className="w-fit"
                                data-tracking-id="prebuild-scm-authenticate"
                            >
                                Authenticate
                            </Button>
                        )}
                    </AlertDescription>
                </Alert>
                {showAuthModal && (
                    <SCMAuthenticationModal
                        repoURL={repoUrl}
                        runnerId={runnerId}
                        authenticationUrl={authDetails.url}
                        patAuth={authDetails.patAuth}
                        scmId={authDetails.scmId}
                        scmName={authDetails.scmName}
                        onClose={() => setShowAuthModal(false)}
                        onClickContinue={handleAuthSuccess}
                        data-track-location={TrackLocations.EnvironmentSCMAuthenticationModal}
                    />
                )}
            </>
        );
    }

    // Show warning for service account
    if (isServiceAccount) {
        return (
            <Alert>
                <IconWarningCircle className="text-content-warning" size="sm" />
                <AlertDescription className="flex flex-col gap-3">
                    <span>
                        This service account doesn&apos;t have access to the repository on the selected runner.
                        Prebuilds will fail until it&apos;s authenticated.
                    </span>
                    <Button variant="primary" size="sm" className="w-fit" asChild>
                        <Link
                            to={serviceAccountDetailsUrl({ id: executor.id })}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-tracking-id="prebuild-scm-configure-service-account"
                        >
                            Configure service account
                            <IconExternalLink size="sm" />
                        </Link>
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    return null;
};
